import { useCallback, useEffect, useRef, useState } from "react";
import { structureApi, type PositionNode } from "../../api/structure";
import type { ApiError } from "../../api/client";
import { formatApiError } from "../../lib/apiError";
import { ancestorIds, findNode, loadWholeForest, patchSlotCounts, setChildren } from "../../lib/positionTree";
import { useToast } from "../../components/Toast";

const SHOW_DELETED_KEY = "bkm.structureShowDeleted";

/**
 * The lazy position tree on `/struktura`: depth 2 up front, one level per expand, optional
 * focus on a branch and "Prikaži obrisane". `enabled: false` (standalone card) loads nothing.
 * Refs mirror the state so async callbacks always see the current forest / focus / flag.
 */
export function usePositionTree(enabled: boolean) {
  const toast = useToast();

  // "Focus on a branch": the tree shows only the sub-tree of this position (deep hierarchies).
  // `chain` = ids from the top of the structure down to it — what the structure scope needs above the forest.
  const [treeRoot, setTreeRoot] = useState<{ id: string; name: string; chain: string[] } | null>(null);
  // "Prikaži obrisane" (2026-09-22): soft-deleted positions shown struck through under their parent.
  const [showDeleted, setShowDeleted] = useState<boolean>(() => {
    try { return localStorage.getItem(SHOW_DELETED_KEY) === "1"; } catch { return false; }
  });
  const showDeletedRef = useRef(showDeleted);
  useEffect(() => {
    showDeletedRef.current = showDeleted;
    try { localStorage.setItem(SHOW_DELETED_KEY, showDeleted ? "1" : "0"); } catch { /* ignore */ }
  }, [showDeleted]);
  const treeRootRef = useRef<string | null>(null);
  useEffect(() => { treeRootRef.current = treeRoot?.id ?? null; }, [treeRoot]);
  const [roots, setRoots] = useState<PositionNode[] | null>(null);
  const rootsRef = useRef<PositionNode[]>([]);
  const [treeError, setTreeError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  const commit = useCallback((next: PositionNode[]) => {
    rootsRef.current = next;
    setRoots(next);
  }, []);

  const expandIds = useCallback((ids: string[]) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      ids.forEach((i) => next.add(i));
      return next;
    });
  }, []);

  const [expandingAll, setExpandingAll] = useState(false);

  /**
   * "Raširi sve": fetches the shown forest four levels deep, then keeps fetching the nodes that
   * are still unloaded (deeper than four) until nothing is left, and opens every node with children.
   */
  const expandAll = useCallback(async () => {
    setExpandingAll(true);
    try {
      const forest = await loadWholeForest(structureApi, treeRootRef.current ?? undefined, showDeletedRef.current);
      commit(forest);
      const open: string[] = [];
      const collect = (l: PositionNode[]) => l.forEach((n) => { if (n.children && n.children.length) { open.push(n.id); collect(n.children); } });
      collect(forest);
      setExpanded(new Set(open));
    } catch (err) {
      toast.error(formatApiError(err as ApiError));
    } finally {
      setExpandingAll(false);
    }
  }, [commit, toast]);

  const collapseAll = useCallback(() => setExpanded(new Set()), []);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setRoots(null);
    structureApi
      .tree({ parentId: treeRoot?.id, depth: 2, includeDeleted: showDeleted || undefined })
      .then((list) => {
        if (cancelled) return;
        commit(list);
        // Open every node whose children arrived with the first request.
        const open: string[] = [];
        const walk = (l: PositionNode[]) => l.forEach((n) => { if (n.children && n.children.length) { open.push(n.id); walk(n.children); } });
        walk(list);
        expandIds(open);
      })
      .catch((err: ApiError) => { if (!cancelled) setTreeError(err.message); });
    return () => { cancelled = true; };
  }, [enabled, commit, expandIds, treeRoot, showDeleted]);

  /** `GET /structure/tree?parentId=&depth=1` → swaps that node's children (keeps hasChildren/childCount in sync). */
  const loadChildren = useCallback(async (parentId: string) => {
    const kids = await structureApi.tree({ parentId, depth: 1, includeDeleted: showDeletedRef.current || undefined });
    commit(setChildren(rootsRef.current, parentId, kids));
    return kids;
  }, [commit]);

  /** Reloads the first level of the shown forest (the focused branch's children when focused), keeping loaded children. */
  const loadTopLevel = useCallback(async () => {
    const fresh = await structureApi.tree({ parentId: treeRootRef.current ?? undefined, depth: 1, includeDeleted: showDeletedRef.current || undefined });
    const prev = new Map(rootsRef.current.map((n) => [n.id, n]));
    commit(fresh.map((n) => {
      const old = prev.get(n.id);
      return old?.children ? { ...n, children: old.children } : n;
    }));
  }, [commit]);

  /**
   * Re-reads the slot counters ("2/13") of `id` and of every ancestor above it: one level request
   * per node on the chain, counters only — loaded children stay as they are.
   */
  const refreshCounts = useCallback(async (id: string) => {
    const chain = ancestorIds(rootsRef.current, id);
    if (chain === null) return;
    const parents = new Set<string | null>();
    for (const nodeId of [...chain, id]) parents.add(findNode(rootsRef.current, nodeId)?.parentId ?? null);
    const levels = await Promise.all([...parents].map((parentId) => structureApi.tree({
      parentId: parentId ?? undefined, depth: 1, includeDeleted: showDeletedRef.current || undefined,
    })));
    commit(patchSlotCounts(rootsRef.current, levels.flat()));
  }, [commit]);

  /**
   * Reloads one level: the first level for `null`, otherwise the children of
   * `parentId` — a no-op for nodes that are not in the loaded tree. A change below a node also
   * changes the counters of that node and of every ancestor, so those are re-read too.
   */
  const refreshChildren = useCallback(async (parentId: string | null) => {
    if (parentId === null || parentId === treeRootRef.current) { await loadTopLevel(); return; }
    if (!findNode(rootsRef.current, parentId)) return;
    await loadChildren(parentId);
    await refreshCounts(parentId);
  }, [loadChildren, loadTopLevel, refreshCounts]);

  const onExpand = useCallback(async (node: PositionNode) => {
    try {
      await loadChildren(node.id);
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }, [loadChildren, toast]);

  /** Siblings of a node in display order — the parent's loaded children, or the shown first level. */
  const siblingsOf = useCallback((node: PositionNode): PositionNode[] => {
    if (node.parentId === null || node.parentId === treeRootRef.current) return rootsRef.current;
    return findNode(rootsRef.current, node.parentId)?.children ?? [];
  }, []);

  /** Loads (and opens) every ancestor in `path` so the selected row is visible. */
  const revealPath = useCallback(async (path: { id: string }[], isCancelled: () => boolean) => {
    for (const p of path) {
      if (isCancelled()) return;
      const node = findNode(rootsRef.current, p.id);
      if (node && node.children === null) await loadChildren(p.id);
    }
    if (!isCancelled()) expandIds(path.map((p) => p.id));
  }, [loadChildren, expandIds]);

  return {
    roots, treeError, expanded, setExpanded, expandIds,
    treeRoot, setTreeRoot, showDeleted, setShowDeleted,
    expandingAll, expandAll, collapseAll,
    onExpand, refreshChildren, siblingsOf, revealPath,
  };
}
